import posthog from "posthog-js"
import { posthogEnabled } from "~/components/providers/Posthog"
import { Search } from "~/components/ui/search"
import { SearchableSelect } from "~/components/ui/searchable-select"
import { notificationKindValues } from "~/lib/enums"
import { ErrorState, EmptyState } from "~/components/screens/StateScreen"
import { NotificationRow } from "~/components/notifications/NotificationRow"
import { InvitationActions } from "~/components/notifications/InvitationActions"
import { TooltipButton } from "~/components/ui/button"
import { formatDistanceToNow } from "date-fns"
import {
	ArrowLeftIcon,
	BellIcon,
	ChevronDownIcon,
	FilterIcon,
	ArchiveXIcon,
	CheckIcon,
	ExternalLinkIcon,
	FullscreenIcon,
	InboxIcon,
	MailIcon,
	MailOpenIcon
} from "lucide-react"
import { motion } from "motion/react"
import { Fragment, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Link } from "~/components/Link"
import { toast } from "sonner"
import { AppHeader } from "~/components/AppHeader"
import { LoadingState } from "~/components/screens/StateScreen"
import { Badge } from "~/components/ui/badge"
import { Button } from "~/components/ui/button"
import { ScrollArea } from "~/components/ui/scroll-area"

import { useIsMobile } from "~/hooks/useMobile"
import { NotificationKind } from "~/lib/enums"
import { useOrganization } from "~/hooks/useOrganization"
import { cn, enumFormat } from "~/lib/utils"
import { api } from "~/trpc/react"

export default function InboxPage() {
	const { organization } = useOrganization()
	const isMobile = useIsMobile(1024)
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [kinds, setKinds] = useState<NotificationKind[]>([])
	const [readState, setReadState] = useState<
		"all" | "unread" | "read" | "archived"
	>("all")
	const [search, setSearch] = useState("")
	const [debouncedSearch, setDebouncedSearch] = useState("")
	useEffect(() => {
		const timeout = setTimeout(() => setDebouncedSearch(search), 250)
		return () => clearTimeout(timeout)
	}, [search])
	const [isListCollapsed, setIsListCollapsed] = useState(false)
	const [skipAutoReadNotificationId, setSkipAutoReadNotificationId] = useState<
		string | null
	>(null)
	const itemId = searchParams.get("item")
	const baseInboxUrl = organization ? `/${organization.slug}/me/inbox` : "."
	const utils = api.useUtils()
	const inbox = api.notifications.listInbox.useInfiniteQuery(
		{
			organizationId: organization?.id ?? "",
			kinds,
			readState,
			search: debouncedSearch
		},
		{
			enabled: !!organization?.id,
			refetchInterval: 30_000,
			getNextPageParam: (page) => {
				const last = page.at(-1)
				return page.length === 50 && last
					? { createdAt: last.createdAt, id: last.id }
					: undefined
			}
		}
	)
	const markRead = api.notifications.markRead.useMutation({
		onSuccess: async () => {
			await Promise.all([
				utils.notifications.listInbox.invalidate(),
				utils.user.getBadges.invalidate()
			])
		},
		onError: () => toast.error("Failed to mark notification as read")
	})
	const toggleRead = api.notifications.toggleRead.useMutation({
		onSuccess: async () => {
			await Promise.all([
				utils.notifications.listInbox.invalidate(),
				utils.user.getBadges.invalidate()
			])
		},
		onError: () => toast.error("Failed to update notification")
	})
	const dismiss = api.notifications.dismiss.useMutation({
		onSuccess: async () => {
			await Promise.all([
				utils.notifications.listInbox.invalidate(),
				utils.user.getBadges.invalidate()
			])
		},
		onError: () => toast.error("Failed to dismiss notification")
	})
	const respondToTeamInvite = api.team.respondToMemberInvitation.useMutation({
		onSuccess: async () => {
			await Promise.all([
				utils.notifications.listInbox.invalidate(),
				utils.user.getBadges.invalidate()
			])
		},
		onError: (error) => toast.error(error.message)
	})
	const markAllRead = api.notifications.markAllRead.useMutation({
		onSuccess: async () => {
			await Promise.all([
				utils.notifications.listInbox.invalidate(),
				utils.user.getBadges.invalidate()
			])
		},
		onError: () => toast.error("Failed to mark notifications as read")
	})
	const archiveAllRead = api.notifications.archiveAllRead.useMutation({
		onSuccess: async () => {
			await Promise.all([
				utils.notifications.listInbox.invalidate(),
				utils.user.getBadges.invalidate()
			])
			navigate(baseInboxUrl)
		},
		onError: () => toast.error("Failed to archive notifications")
	})
	const notifications = inbox.data?.pages.flat() ?? []
	const localNotification = itemId
		? notifications.find((notification) => notification.id === itemId)
		: undefined
	const linkedInbox = api.notifications.listInbox.useQuery(
		{
			organizationId: organization?.id ?? "",
			itemId: itemId ?? undefined
		},
		{
			enabled:
				!!organization?.id && !!itemId && !localNotification && !inbox.isLoading
		}
	)
	const linkedNotification =
		localNotification ??
		(itemId
			? linkedInbox.data?.find((notification) => notification.id === itemId)
			: undefined)
	const visibleNotifications = notifications
	const selectedNotification = itemId ? linkedNotification : undefined
	const getTeamInvitationId = (data: unknown) => {
		if (!data || typeof data !== "object" || Array.isArray(data))
			return undefined
		const invitationId =
			"teamMemberInvitationId" in data ? data.teamMemberInvitationId : undefined
		return typeof invitationId === "string" ? invitationId : undefined
	}
	const selectedTeamInvitationId =
		selectedNotification?.kind === NotificationKind.TeamInvite
			? getTeamInvitationId(selectedNotification.data)
			: undefined
	const handleTeamInviteResponse = async (
		invitationId: string,
		notificationId: string,
		response: "accept" | "decline"
	) => {
		if (!organization?.id) return
		try {
			const result = await respondToTeamInvite.mutateAsync({
				organizationId: organization.id,
				invitationId,
				response
			})
			await dismiss.mutateAsync({
				organizationId: organization.id,
				notificationId
			})
			toast.success(
				response === "accept"
					? "Team invitation accepted"
					: "Invitation declined"
			)
			if (result.destination) {
				navigate(
					`/${result.destination.orgSlug}/${result.destination.teamSlug}`
				)
			} else {
				navigate(baseInboxUrl)
			}
		} catch {
			// The mutation callbacks already report errors.
		}
	}
	const isDetailLoading =
		!!itemId && !linkedNotification && linkedInbox.isLoading
	const nextNotification = selectedNotification
		? visibleNotifications[
				visibleNotifications.findIndex(
					(notification) => notification.id === selectedNotification.id
				) + 1
			]
		: undefined

	useEffect(() => {
		if (!itemId) setIsListCollapsed(false)
	}, [itemId])

	useEffect(() => {
		if (skipAutoReadNotificationId && skipAutoReadNotificationId !== itemId) {
			setSkipAutoReadNotificationId(null)
		}
	}, [itemId, skipAutoReadNotificationId])

	useEffect(() => {
		if (!selectedNotification?.id) return

		const frame = requestAnimationFrame(() => {
			document
				.getElementById(`notification-${selectedNotification.id}`)
				?.scrollIntoView({ block: "center" })
		})

		return () => cancelAnimationFrame(frame)
	}, [selectedNotification?.id])

	useEffect(() => {
		if (
			!organization?.id ||
			!selectedNotification ||
			selectedNotification.readAt ||
			selectedNotification.dismissedAt ||
			skipAutoReadNotificationId === selectedNotification.id
		) {
			return
		}

		const timeout = window.setTimeout(() => {
			markRead.mutate({
				organizationId: organization.id,
				notificationId: selectedNotification.id
			})
		}, 2_000)

		return () => window.clearTimeout(timeout)
	}, [
		organization?.id,
		selectedNotification,
		skipAutoReadNotificationId,
		markRead.mutate
	])

	if (!organization) {
		return <LoadingState className="bg-transparent" />
	}

	return (
		<div className="flex h-full flex-col bg-background-secondary">
			<AppHeader
				actions={
					<Button asChild variant="outline" size="sm">
						<Link
							to={`/${organization.slug}/me/settings#notifications`}
							state={{ fromInbox: true }}
						>
							<BellIcon className="size-4" />
							<span className="hidden sm:inline">Notification preferences</span>
							<span className="sr-only sm:hidden">
								Notification preferences
							</span>
						</Link>
					</Button>
				}
			>
				<h1 className="truncate font-medium text-text-main">My Inbox</h1>
			</AppHeader>
			<div className="flex min-h-0 flex-1 bg-background">
				<motion.section
					layout
					aria-hidden={!isMobile && isListCollapsed}
					transition={{ duration: 0.2, ease: "easeInOut" }}
					className={cn(
						"flex min-h-0 w-full shrink-0 flex-col overflow-hidden border-stroke border-r bg-background-secondary lg:w-96 xl:w-[30rem]",
						isListCollapsed && "lg:w-0 lg:border-r-0 xl:w-0",
						itemId && "hidden lg:flex"
					)}
				>
					<div className="flex h-12 shrink-0 items-center justify-between border-stroke border-b px-4">
						<div className="flex min-w-0 items-center gap-2">
							<InboxIcon className="size-4 text-text-small" />
							<span className="truncate font-medium text-sm text-text-main">
								Notifications
							</span>
						</div>
						<div className="flex items-center gap-1.5">
							<TooltipButton
								variant="ghost"
								size="icon"
								tooltip="Mark all notifications read"
								aria-label="Mark all notifications read"
								disabled={markAllRead.isPending}
								onClick={() =>
									markAllRead.mutate({ organizationId: organization.id })
								}
							>
								<CheckIcon className="size-4" />
							</TooltipButton>
							<TooltipButton
								variant="ghost"
								size="icon"
								tooltip="Archive all read notifications"
								aria-label="Archive all read notifications"
								disabled={archiveAllRead.isPending}
								onClick={() =>
									archiveAllRead.mutate({ organizationId: organization.id })
								}
							>
								<ArchiveXIcon className="size-4" />
							</TooltipButton>
						</div>
					</div>
					<div className="flex flex-col gap-3 border-b border-stroke p-3">
						<div
							role="group"
							aria-label="Notification status"
							className="flex flex-wrap gap-1"
						>
							{(["all", "unread", "read", "archived"] as const).map((value) => (
								<Button
									key={value}
									size="sm"
									variant={readState === value ? "outline" : "ghost"}
									aria-pressed={readState === value}
									onClick={() => {
										setReadState(value)
										if (posthogEnabled)
											posthog.capture("inbox_filter_changed", {
												filter: "read_state",
												value
											})
									}}
								>
									{value === "all" ? "All" : enumFormat(value)}
								</Button>
							))}
						</div>
						<div className="flex items-center gap-2">
							<SearchableSelect
								multiple
								value={kinds}
								options={notificationKindValues.map((kind) => ({
									value: kind,
									label: enumFormat(kind)
								}))}
								onChange={(values) => {
									if (posthogEnabled)
										posthog.capture("inbox_filter_changed", {
											filter: "type",
											values
										})
									setKinds(
										notificationKindValues.filter((kind) =>
											values.includes(kind)
										)
									)
								}}
							>
								<Button variant="outline" size="sm">
									<FilterIcon className="size-4" />
									{kinds.length === 1
										? enumFormat(kinds[0]!)
										: kinds.length
											? `${kinds.length} types`
											: "All types"}
									<ChevronDownIcon className="size-3" />
								</Button>
							</SearchableSelect>
							{(kinds.length > 0 || search || readState !== "all") && (
								<Button
									variant="ghost"
									size="sm"
									onClick={() => {
										setKinds([])
										setSearch("")
										setReadState("all")
									}}
								>
									Clear filters
								</Button>
							)}
						</div>
						<Search
							mode="input"
							value={search}
							onChange={(value) => setSearch(value.slice(0, 200))}
							placeholder="Search notifications…"
						/>
					</div>
					<ScrollArea className="min-h-0 flex-1">
						<div className="p-2">
							{inbox.isLoading ? (
								<LoadingState className="bg-transparent" />
							) : inbox.error ? (
								<ErrorState
									title="Unable to load notifications"
									description={inbox.error.message}
									actions={
										<Button
											variant="outline"
											onClick={() => void inbox.refetch()}
										>
											Try again
										</Button>
									}
								/>
							) : visibleNotifications.length === 0 ? (
								<EmptyState
									scope="inline"
									icon={<InboxIcon className="size-5" />}
									title={
										kinds.length || search || readState !== "all"
											? "No matching notifications"
											: "You’re all caught up"
									}
								/>
							) : (
								visibleNotifications.map((notification) => {
									const invitationId =
										notification.kind === NotificationKind.TeamInvite
											? getTeamInvitationId(notification.data)
											: undefined
									return (
										<Fragment key={notification.id}>
											<NotificationRow
												notification={notification}
												selected={notification.id === selectedNotification?.id}
												to={`${baseInboxUrl}?item=${notification.id}`}
											/>
											{isMobile && invitationId ? (
												<div className="flex justify-end px-3 pb-3">
													<InvitationActions
														pending={respondToTeamInvite.isPending}
														compact
														onRespond={(response) =>
															void handleTeamInviteResponse(
																invitationId,
																notification.id,
																response
															)
														}
													/>
												</div>
											) : null}
										</Fragment>
									)
								})
							)}
							{inbox.hasNextPage && (
								<Button
									variant="ghost"
									className="w-full"
									disabled={inbox.isFetchingNextPage}
									onClick={() => void inbox.fetchNextPage()}
								>
									{inbox.isFetchingNextPage ? "Loading…" : "Load more"}
								</Button>
							)}
						</div>
					</ScrollArea>
				</motion.section>
				<motion.section
					layout
					transition={{ duration: 0.2, ease: "easeInOut" }}
					className={cn(
						"min-h-0 min-w-0 flex-1 flex-col border-stroke border-t bg-background lg:flex lg:border-t-0",
						itemId ? "flex" : "hidden"
					)}
				>
					{isDetailLoading ? (
						<LoadingState className="h-full min-h-0 bg-transparent" />
					) : itemId && !linkedNotification ? (
						<EmptyState
							icon={<InboxIcon className="size-8 text-text-small" />}
							title="Notification unavailable"
							description={<>This notification is no longer in your inbox.</>}
							actions={
								<Button asChild variant="outline" size="sm">
									<Link to={baseInboxUrl}>Clear link</Link>
								</Button>
							}
						/>
					) : selectedNotification ? (
						<>
							<div className="flex h-12 shrink-0 items-center justify-between gap-3 border-stroke border-b px-5">
								<div className="min-w-0 truncate text-text-small text-xs">
									{enumFormat(selectedNotification.kind)} ·{" "}
									{formatDistanceToNow(selectedNotification.createdAt, {
										addSuffix: true
									})}
								</div>
								<div className="flex shrink-0 items-center gap-1">
									<Button
										asChild
										variant="ghost"
										size="icon"
										className="lg:hidden"
										aria-label="Back to inbox"
									>
										<Link to={baseInboxUrl}>
											<ArrowLeftIcon className="size-4" />
										</Link>
									</Button>
									<TooltipButton
										variant="ghost"
										size="icon"
										onClick={() =>
											setIsListCollapsed((collapsed) => !collapsed)
										}
										className={cn(
											"hidden lg:inline-flex",
											isListCollapsed && "bg-active"
										)}
										tooltip={isListCollapsed ? "Show list" : "Focus detail"}
									>
										<FullscreenIcon className="size-4" />
									</TooltipButton>
									<TooltipButton
										variant="ghost"
										size="icon"
										disabled={toggleRead.isPending}
										onClick={() => {
											if (!organization?.id) return
											setSkipAutoReadNotificationId(
												selectedNotification.readAt
													? selectedNotification.id
													: null
											)
											toggleRead.mutate({
												organizationId: organization.id,
												notificationId: selectedNotification.id
											})
										}}
										tooltip={
											selectedNotification.readAt ? "Mark unread" : "Mark read"
										}
									>
										{selectedNotification.readAt ? (
											<MailIcon className="size-4" />
										) : (
											<MailOpenIcon className="size-4" />
										)}
									</TooltipButton>
									<TooltipButton
										variant="ghost"
										size="icon"
										disabled={
											dismiss.isPending || !!selectedNotification.dismissedAt
										}
										onClick={() =>
											organization?.id &&
											dismiss.mutate(
												{
													organizationId: organization.id,
													notificationId: selectedNotification.id
												},
												{
													onSuccess: () =>
														navigate(
															nextNotification
																? `${baseInboxUrl}?item=${nextNotification.id}`
																: baseInboxUrl
														)
												}
											)
										}
										tooltip="Dismiss"
									>
										<ArchiveXIcon className="size-4" />
									</TooltipButton>
								</div>
							</div>
							<ScrollArea className="min-h-0 flex-1">
								<article className="mx-auto max-w-3xl px-5 py-8 sm:px-8 sm:py-10">
									<div className="mb-5 flex flex-wrap items-center gap-2">
										<Badge
											variant={
												selectedNotification.readAt ? "secondary" : "default"
											}
										>
											{selectedNotification.readAt ? "Read" : "Unread"}
										</Badge>
										{selectedNotification.dismissedAt && (
											<Badge variant="secondary">Dismissed</Badge>
										)}
									</div>
									<h2 className="text-balance font-semibold text-xl sm:text-2xl text-text-main tracking-tight">
										{selectedNotification.title}
									</h2>
									<p className="mt-6 whitespace-pre-wrap text-[15px] text-text-sub leading-7">
										{selectedNotification.body}
									</p>
									{selectedTeamInvitationId ? (
										<div className="mt-8">
											<InvitationActions
												pending={respondToTeamInvite.isPending}
												onRespond={(response) =>
													void handleTeamInviteResponse(
														selectedTeamInvitationId,
														selectedNotification.id,
														response
													)
												}
											/>
										</div>
									) : selectedNotification.actionUrl ? (
										<Button asChild className="mt-8" size="sm">
											<Link to={selectedNotification.actionUrl}>
												Open
												<ExternalLinkIcon className="size-4" />
											</Link>
										</Button>
									) : null}
								</article>
							</ScrollArea>
						</>
					) : visibleNotifications.length > 0 ? (
						<EmptyState
							icon={<InboxIcon className="size-8 text-text-small" />}
							title="Select a notification"
						/>
					) : (
						<EmptyState
							icon={<InboxIcon className="size-8 text-text-small" />}
							title={
								kinds.length || search || readState !== "all"
									? "No matching notifications"
									: "All clear"
							}
						/>
					)}
				</motion.section>
			</div>
		</div>
	)
}
